/**
 * useQueueSync — HTTP fallback for queue persistence.
 *
 * Real-time cross-device sync is handled by ClientSyncHandler over WebSocket.
 * This hook only pushes state to the QueueSession DO via HTTP as a backup,
 * ensuring the server has a recent snapshot even if the WebSocket path fails.
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getCurrentPlaybackSnapshot } from '../transport/PlaybackSnapshot';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { pushQueueSnapshotToServer } from '../services/queueSyncPush';

const SYNC_DEBOUNCE_MS = 4000;
const POSITION_SYNC_INTERVAL_MS = 120000;

function mapQueueForServer(queue: any[]): any[] {
  return queue.slice(0, 200).map((item: any) => {
    const t = item?.track || item;
    const mapped: Record<string, any> = {
      id: t.id || '',
      title: t.title || '',
      artist: t.artist || '',
      album: t.album || '',
      artUrl: t.artworkUrl || t.artUrl || t.coverUrl || t.cover || t.imageUrl || t.artwork || '',
      formatId: t.formatId || 27,
      qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || '',
      duration: t.duration || 0,
      source: t.source || 'library',
    };
    if (t.source === 'local_library') {
      mapped.file_path = t.file_path || '';
      mapped.machine_id = t.machine_id || '';
      mapped.format = t.format || '';
    }
    return mapped;
  });
}

export function useQueueSync(): void {
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const positionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPushRef = useRef<string>('');

  useEffect(() => {
    const schedulePush = () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        const snap = getCurrentPlaybackSnapshot();

        if (snap.playbackState === 'idle' && snap.queue.length === 0 && !snap.isRadio) return;

        const syncHandler = getSyncHandler();
        const payload: Record<string, any> = {
          playbackState: snap.playbackState,
          activeSource: snap.activeSource,
          currentTrackId: snap.track?.id || null,
          currentTrack: snap.track ? {
            id: snap.track.id,
            title: snap.track.title,
            artist: snap.track.artist,
            album: snap.track.album,
            artUrl: snap.track.artUrl,
            formatId: snap.track.formatId || 27,
            qobuzTrackId: snap.track.qobuzTrackId || '',
            duration: snap.track.duration,
          } : undefined,
          queueIndex: snap.queueIndex,
          position: snap.position,
          duration: snap.duration,
          volume: snap.volume,
          repeatMode: snap.repeatMode,
          shuffled: snap.shuffled,
          shuffleOrder: snap.shuffleOrder,
          rendererId: snap.renderer?.id || null,
          rendererName: snap.renderer?.name || null,
          queue: mapQueueForServer(snap.queue),
          controllerId: syncHandler.getDeviceId(),
        };

        const fingerprint = `${payload.activeSource}|${payload.currentTrackId}|${payload.queueIndex}|${payload.playbackState}|${snap.queue.length}|${payload.rendererId || 'local'}`;
        if (fingerprint === lastPushRef.current) return;
        lastPushRef.current = fingerprint;

        pushQueueSnapshotToServer(payload);
      }, SYNC_DEBOUNCE_MS);
    };

    const unsubscribe = useMaestroStore.subscribe((state, prev) => {
      const trackChanged = state.currentTrack?.id !== prev?.currentTrack?.id;
      const stateChanged = state.playbackState !== prev?.playbackState;
      const queueChanged = state.queue.length !== prev?.queue.length
        || state.queueIndex !== prev?.queueIndex;
      const sourceChanged = state.activeSource !== prev?.activeSource;

      if (trackChanged || stateChanged || queueChanged || sourceChanged) {
        schedulePush();
      }
    });

    positionTimerRef.current = setInterval(() => {
      const snap = getCurrentPlaybackSnapshot();
      if (snap.playbackState !== 'playing') return;

      pushQueueSnapshotToServer({
        position: snap.position,
        duration: snap.duration,
        playbackState: 'playing',
        activeSource: snap.activeSource,
      });
    }, POSITION_SYNC_INTERVAL_MS);

    return () => {
      unsubscribe();
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      if (positionTimerRef.current) clearInterval(positionTimerRef.current);
    };
  }, []);
}
